import styled from '@emotion/styled';
import type { IAirport } from '@move-frontend/dap';
import moment from 'moment';
import React from 'react';
import { FormattedTime, type ITheme } from '../../../../../../ui';
import themed from '../../../../../../ui/themer/themed';
import Responsive from '../../../../../../ui/utils/Responsive';
import { withTranslate } from '../../../../../shared/i18n/translate';
import { isCanceledFlight } from '../../../../shared/flight/domain/use-cases/helpers/isCanceledFlight';
import type { IFlightPublicStatus } from '../../../../shared/status/domain/models/IFlightPublicStatus';
import { Column, ColumnHeader } from './Column';

interface IProps {
	viaAirport: string;
	airport?: IAirport;
	codeShareFlightNumber: string;
	status: IFlightPublicStatus;
	bestKnownTimestamp: number;
	scheduledTimestamp: number;
	arrival: boolean;
	role?: 'columnheader' | 'cell';
}

const Airport = styled.span<{}, ITheme>`
	display: flex;
	font-weight: 700;

	@media (max-width: 360px) {
		font-weight: normal;
	}
`;

const AirportCity = styled.span<{}, ITheme>`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const AirportCode = styled.span<{}, ITheme>`
	padding-left: 0.2em;
`;

const FlightNumber = styled.div<{}, ITheme>`
	font-size: 16px;
	color: ${themed(({ color }) => color.text.light)};
`;

const CanceledStatus = styled.div<{}, ITheme>`
	font-size: 16px;
	color: ${themed(({ color }) => color.state.error)};
	display: none;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: block;
	}
`;

const BestKnownTime = styled.div<{}, ITheme>`
	font-size: 16px;
	color: ${themed(({ color }) => color.text.light)};
	line-height: 1;
`;

export const StyledDescriptionColumn = styled(
	({ role = 'columnheader', ...props }) =>
		role === 'columnheader' ? (
			<ColumnHeader {...props} />
		) : (
			<Column {...props} />
		),
)`
	min-width: 200px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		min-width: 0;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobile')}) {
		&,
		${FlightNumber}, ${CanceledStatus}, ${BestKnownTime} {
			font-size: 14px;
		}
	}
`;

export const DescriptionColumn = withTranslate<IProps>(
	({
		t,
		airport,
		viaAirport,
		codeShareFlightNumber,
		status,
		arrival,
		bestKnownTimestamp,
		scheduledTimestamp,
		role,
	}) => {
		const isCanceled = isCanceledFlight(status);

		const bestKnownTime = moment.unix(bestKnownTimestamp);
		const scheduledTime = moment.unix(scheduledTimestamp);

		const isTimeEqual = bestKnownTime.isSame(scheduledTime, 'minute');
		const showBestKnownTime = arrival && !isCanceled && !isTimeEqual;

		return (
			<StyledDescriptionColumn role={role}>
				<Airport>
					{airport && airport.city ? (
						<>
							<AirportCity>{airport.city}</AirportCity>
							<> </>
							<AirportCode>({viaAirport})</AirportCode>
						</>
					) : (
						`(${viaAirport})`
					)}
				</Airport>
				<FlightNumber>{codeShareFlightNumber}</FlightNumber>
				{isCanceled && <CanceledStatus>{status}</CanceledStatus>}
				{showBestKnownTime && (
					<BestKnownTime>
						{t('web_flight_list_expected_landing')}
						<> </>
						<FormattedTime value={bestKnownTimestamp} />
					</BestKnownTime>
				)}
			</StyledDescriptionColumn>
		);
	},
);
